import {
    zodLegalFirstNameValidation,
    zodLegalLastNameValidation,
} from '@fruitful/fruitils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    BodyMediumRegular,
    ControlledInput,
    Form,
    H5Emphasized,
    isWeb,
    View,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { useAuthedUser } from '../../../../common/providers/AuthedUserProvider';
import { toCardFriendlyName } from '../../../../common/utils/cardUtils';
import {
    formatWithMask,
    PHONE_NUMBER_MASK,
    VALID_PHONE_NUMBER_MASK,
} from '../../../../common/utils/maskUtils';
import type { RunKYCVerifyBody } from '../../../hooks/kyc/useRunKycVerify';
import KycButton from './KycButton';

const contactInfoSchema = z.object({
    firstName: zodLegalFirstNameValidation,
    lastName: zodLegalLastNameValidation,
    email: z.string().email(),
    phoneNumber: z
        .string()
        .regex(VALID_PHONE_NUMBER_MASK, 'Please enter a valid phone number'),
});

type ContactInfoSchema = z.infer<typeof contactInfoSchema>;

interface UpdateContactInfoProps {
    onPressNext: (formData?: Partial<RunKYCVerifyBody>) => void;
}

const ContactInfo = ({ onPressNext }: UpdateContactInfoProps) => {
    const user = useAuthedUser();

    const { firstName, lastName, email, phoneNumber } = user;

    const normalizedFirstName = toCardFriendlyName(firstName ?? '');
    const normalizedLastName = toCardFriendlyName(lastName ?? '');

    const { masked: formattedPhoneNumber, unmasked } = formatWithMask(
        PHONE_NUMBER_MASK,
        phoneNumber != null ? phoneNumber.substring(2) : '', // Remove country code (+1)
    );

    const [unmaskedPhoneNumber, setUnmaskedPhoneNumber] = useState(unmasked);

    const {
        register,
        control,
        setFocus,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<ContactInfoSchema>({
        resolver: zodResolver(contactInfoSchema),
        defaultValues: {
            firstName: normalizedFirstName,
            lastName: normalizedLastName,
            email,
            phoneNumber: formattedPhoneNumber,
        },
    });

    const onSubmit: SubmitHandler<ContactInfoSchema> = ({
        firstName,
        lastName,
    }) => {
        onPressNext({
            firstName,
            lastName,
            email,
            phoneNumber: `+1${unmaskedPhoneNumber}`, // Add country code back in
        });
    };

    return (
        <YStack
            flex={1}
            justifyContent={isWeb ? 'flex-start' : 'space-between'}
        >
            <View>
                <H5Emphasized marginBottom={8}>
                    Confirm that the information we have is correct
                </H5Emphasized>
                <BodyMediumRegular color="$gray900" marginBottom={24}>
                    This is what you entered when you signed up. Make sure the
                    details match what&apos;s shown on your ID.
                </BodyMediumRegular>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <YStack gap={12}>
                        <ControlledInput
                            name="firstName"
                            control={control}
                            register={register}
                            errors={errors}
                            placeholder="First name"
                            autoCapitalize="words"
                            autoComplete="given-name"
                            enterKeyHint="next"
                            onSubmitEditing={() => {
                                setFocus('lastName');
                            }}
                        />
                        <ControlledInput
                            name="lastName"
                            control={control}
                            register={register}
                            errors={errors}
                            placeholder="Last name"
                            autoCapitalize="words"
                            autoComplete="family-name"
                            enterKeyHint="next"
                            onSubmitEditing={() => {
                                setFocus('email');
                            }}
                        />
                        <ControlledInput
                            name="email"
                            control={control}
                            register={register}
                            errors={errors}
                            disabled
                            placeholder="Email address"
                            autoComplete="email"
                            enterKeyHint="next"
                            inputMode="email"
                            onSubmitEditing={() => {
                                setFocus('phoneNumber');
                            }}
                        />
                        <ControlledInput
                            name="phoneNumber"
                            control={control}
                            register={register}
                            errors={errors}
                            formatValueOnChange={(_, value) => {
                                const { masked, unmasked } = formatWithMask(
                                    PHONE_NUMBER_MASK,
                                    value,
                                );
                                setUnmaskedPhoneNumber(unmasked);
                                return masked;
                            }}
                            placeholder="Phone number"
                            autoComplete="tel"
                            enterKeyHint="done"
                            keyboardType="numbers-and-punctuation"
                            onSubmitEditing={handleSubmit(onSubmit)}
                        />
                    </YStack>
                </Form>
            </View>
            <KycButton onPress={handleSubmit(onSubmit)} disabled={!isValid} />
        </YStack>
    );
};

export default ContactInfo;
